/**
 * The two pieces of arithmetic behind the arrange step's dragging.
 *
 * The pointer plumbing — pointer capture, the floating row under the finger, the autoscroll at the edges —
 * belongs to the component and only a browser can say whether it feels right. What is left underneath is
 * index arithmetic, and both halves of it hide an off-by-one that is invisible on screen until the user
 * notices a stop landed one row away from where they let go of it. That is the part worth pulling out into
 * a file a test can reach: pure functions over numbers, no DOM, no React.
 */

/**
 * The list with one item moved from `from` to `to`, or null when that move means nothing.
 *
 * `to` is the index the item ends up at, not the index it is inserted before. Dropping a row onto the last
 * slot means `to` is `items.length - 1`, which is the reading the drag handler has already done its pixel
 * arithmetic for; the alternative convention would make the last two slots both mean "the end".
 *
 * Null is the answer for a move that cannot have an effect — an index outside the list, or the item's own
 * position — and it is deliberately not the unchanged list. A caller holding a copy gets null and knows
 * there is nothing to save, which is what keeps a drag that ends where it started from writing an identical
 * arrangement back to the API and re-rendering the screen.
 *
 * The input is never mutated: the returned array is a copy, so a caller may keep rendering the list it
 * passed while it holds the next one.
 */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] | null {
  // `Number.isInteger` as well as the range: an index that arrived from pixel arithmetic can be NaN or a
  // fraction, and both are "outside the list" even though neither compares as such.
  const inside = (index: number) => Number.isInteger(index) && index >= 0 && index < items.length;

  if (!inside(from) || !inside(to) || from === to) {
    return null;
  }

  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);

  return next;
}

/**
 * The index an inserted row belongs at, given each existing row's vertical extent and the pointer's y.
 *
 * The rule is the usual one: the insertion point is the first row whose vertical midpoint is below the
 * pointer, so the pointer in the top half of a row inserts above it and in the bottom half inserts below
 * it. A pointer exactly on a midpoint counts as below — the midpoint is not *greater than* `y` — so the
 * row after it wins. Both sides of that boundary are pinned by tests, because swapping `>` for `>=` moves
 * every drop by one row and nothing on the screen would say which reading was intended.
 *
 * The extents arrive as numbers rather than as elements because the pointer is in viewport coordinates,
 * and the component has already read each row's rectangle in that same space.
 */
export function dropIndexAt(rows: ReadonlyArray<{ top: number; bottom: number }>, y: number): number {
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index]!;
    if (y < (row.top + row.bottom) / 2) {
      return index;
    }
  }

  return rows.length;
}
